<template>
  <div class="mx-4 md:mx-8 py-5 pb-10">
    <!-- Cabecera: título, resumen del curso y acción principal -->
    <div class="flex flex-wrap items-end justify-between gap-3 mb-6">
      <div>
        <h2 class="text-lg font-semibold text-gray-900">{{ $t('projects.course_content') }}</h2>
        <p v-if="sections.length" class="mt-0.5 text-sm text-gray-600 tabular-nums">
          {{ $t('projects.sections_count', { count: sections.length }, sections.length) }} · {{ $t('projects.lessons_count', { count: totalLessons }, totalLessons) }}
        </p>
        <div v-if="sections.length > 1" class="mt-2 flex gap-1">
          <button type="button" v-on:click="setAllCollapsed(false)" :disabled="!collapsedSections.length"
                  class="rounded-md px-2 py-1 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-bpblue">
            {{ $t('projects.expand_all_sections') }}
          </button>
          <button type="button" v-on:click="setAllCollapsed(true)" :disabled="collapsedSections.length === sections.length"
                  class="rounded-md px-2 py-1 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-bpblue">
            {{ $t('projects.collapse_all_sections') }}
          </button>
        </div>
      </div>
      <button type="button" v-on:click="openCreateSection" :disabled="general.creatingSection"
              class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-bpblue hover:bg-blue-500 active:scale-[0.98] disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-bpblue">
        <font-awesome-icon icon="plus" class="-ml-1 mr-2 h-4 w-4" aria-hidden="true"/>
        {{ $t('projects.new_section') }}
      </button>
    </div>

    <!-- Resultado de reordenar, publicar o renombrar: se anuncia sin toast (FE-063). -->
    <p class="sr-only" role="status" aria-live="polite">{{ liveMessage }}</p>

    <!-- Nueva sección -->
    <div v-show="general.createSection" class="mb-6 rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
      <div class="px-4 py-4 sm:px-6">
        <label for="new-section-title" class="block text-sm font-medium text-gray-900">{{ $t('projects.new_section') }}</label>
        <input id="new-section-title" ref="newSectionTitle" v-model="general.section.title" type="text" :disabled="general.creatingSection"
               :placeholder="$t('projects.section_title_placeholder')" @keyup.enter="saveSection" @keyup.esc="cancelAddSection"
               :aria-invalid="general.sectionError ? 'true' : 'false'" aria-describedby="new-section-error"
               class="mt-2 block w-full rounded-md border-gray-300 shadow-sm placeholder-gray-500 focus:border-bpblue focus:ring-bpblue sm:text-sm">
        <p v-if="general.sectionError" id="new-section-error" class="mt-2 text-sm text-red-600">{{ general.sectionError }}</p>
      </div>
      <div class="flex justify-end gap-2 border-t border-gray-100 bg-gray-50 px-4 py-3 sm:px-6 rounded-b-lg">
        <button type="button" v-on:click="cancelAddSection" :disabled="general.creatingSection"
                class="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-bpblue">
          {{ $t('general.cancel') }}
        </button>
        <button type="button" v-on:click="saveSection" :disabled="general.creatingSection"
                class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-bpblue hover:bg-blue-500 active:scale-[0.98] disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-bpblue">
          <font-awesome-icon v-if="general.creatingSection" class="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" icon="spinner"/>
          {{ $t('projects.create_section') }}
        </button>
      </div>
    </div>

    <!-- Curso sin secciones -->
    <div v-if="!sections.length && !general.createSection"
         class="rounded-lg border-2 border-dashed border-gray-300 px-6 py-14 text-center">
      <font-awesome-icon icon="list-ul" class="mx-auto h-10 w-10 text-gray-400" aria-hidden="true"/>
      <p class="mt-3 text-sm font-medium text-gray-900">{{ $t('projects.empty_sections_title') }}</p>
      <p class="mt-1 text-sm text-gray-600">{{ $t('projects.empty_sections_text') }}</p>
      <button type="button" v-on:click="openCreateSection"
              class="mt-5 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-bpblue hover:bg-blue-500 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-bpblue">
        {{ $t('projects.new_section') }}
      </button>
    </div>

    <!-- Secciones con sus lecciones -->
    <vue-draggable
      v-model="projectsStore.project.sections"
      handle=".section-drag-handle"
      ghost-class="drag-ghost"
      chosen-class="drag-chosen"
      :animation="150"
      :disabled="!features.reorder || general.savingOrder"
      class="space-y-6"
      @start="onDragStart"
      @end="onSectionDragEnd"
    >
      <section v-for="(section, sectionIndex) in sections" :key="section.uuid"
               :aria-labelledby="'section-heading-' + section.uuid"
               class="rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <!-- Cabecera de la sección -->
        <div :id="'section-header-' + section.uuid" class="flex flex-wrap items-center gap-x-3 gap-y-2 bg-gray-50 px-3 py-3 sm:px-4"
             :class="isCollapsed(section) && scheduleSectionUuid !== section.uuid ? 'rounded-lg' : 'rounded-t-lg border-b border-gray-100'">
          <!-- En móvil, título en una fila completa y resumen y acciones debajo (FE-064). -->
          <div class="flex w-full min-w-0 items-center gap-2 md:w-auto md:flex-1">
            <button type="button" v-on:click="toggleCollapsed(section)"
                    :aria-expanded="isCollapsed(section) ? 'false' : 'true'" :aria-controls="'section-lessons-' + section.uuid"
                    :aria-label="$t(isCollapsed(section) ? 'projects.expand_section' : 'projects.collapse_section', { title: section.title })"
                    :title="$t(isCollapsed(section) ? 'projects.expand_section' : 'projects.collapse_section', { title: section.title })"
                    class="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-bpblue md:h-8 md:w-8">
              <font-awesome-icon icon="chevron-down" class="h-3.5 w-3.5 motion-safe:transition-transform" :class="{ '-rotate-90': isCollapsed(section) }" aria-hidden="true"/>
            </button>
            <template v-if="features.reorder">
              <reorder-buttons-component
                :can-move-up="sectionIndex > 0"
                :can-move-down="sectionIndex < sections.length - 1"
                :up-label="$t('projects.move_section_up')" :down-label="$t('projects.move_section_down')"
                :disabled="general.savingOrder"
                @move-up="moveSection(sectionIndex, -1)" @move-down="moveSection(sectionIndex, 1)"/>
              <span class="section-drag-handle flex h-11 w-8 flex-shrink-0 cursor-grab items-center justify-center rounded-md text-gray-500 hover:text-gray-700 active:cursor-grabbing md:h-auto md:w-auto md:p-1" :title="$t('projects.drag_section')" aria-hidden="true">
                <font-awesome-icon icon="grip-vertical" class="h-4 w-4" aria-hidden="true"/>
              </span>
            </template>
            <span class="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-white text-xs font-semibold text-gray-600 ring-1 ring-gray-200 tabular-nums" aria-hidden="true">
              {{ sectionIndex + 1 }}
            </span>
            <div class="min-w-0 flex-1">
              <label :id="'section-heading-' + section.uuid" :for="'section-title-' + section.uuid" class="sr-only">
                {{ $t('projects.section_number', { number: sectionIndex + 1 }) }}
              </label>
              <!-- Clic para editar: fuera de edición el título se lee entero, sin recortar. -->
              <button v-if="!isEditingSectionTitle(section)" :id="'section-title-display-' + section.uuid" type="button" v-on:click="startEditSectionTitle(section)"
                      :aria-label="$t('projects.rename_section', { title: section.title })"
                      class="block w-full break-words rounded-md px-2 py-1 text-left text-base font-semibold text-gray-900 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-bpblue">
                {{ section.title }}
              </button>
              <input v-else :id="'section-title-' + section.uuid" v-model="section.title" type="text"
                     @keyup.enter="isSectionDirty(section) && editSection(section)"
                     @keydown.esc.prevent="revertSectionTitle(section)" @blur="stopEditSectionTitle(section)"
                     class="block w-full truncate rounded-md border-transparent bg-transparent px-2 py-1 text-base font-semibold text-gray-900 hover:border-gray-300 hover:bg-white focus:border-bpblue focus:bg-white focus:ring-bpblue">
            </div>
            <button v-if="isSectionDirty(section)" type="button" v-on:click="editSection(section)"
                    :disabled="general.savingSectionUuid === section.uuid || !section.title.trim()"
                    class="inline-flex flex-shrink-0 items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-bpblue hover:bg-blue-500 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-bpblue">
              <font-awesome-icon v-if="general.savingSectionUuid === section.uuid" class="mr-2 h-4 w-4 animate-spin" icon="spinner"/>
              {{ $t('general.save') }}
            </button>
          </div>

          <div class="flex w-full items-center gap-3 pl-12 md:w-auto md:flex-shrink-0 md:pl-0">
            <div class="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
            <span class="text-xs text-gray-600 tabular-nums">{{ sectionSummary(section) }}</span>
            <button v-if="dripLabel(section)" type="button" v-on:click="openSchedule(section)"
                    :aria-label="$t('projects.drip_edit_of', { title: section.title, schedule: dripLabel(section) })"
                    class="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-bpblue">
              <font-awesome-icon icon="clock" class="h-3 w-3" aria-hidden="true"/>
              {{ dripLabel(section) }}
            </button>
            </div>
            <div class="ml-auto flex flex-shrink-0 items-center gap-2 md:gap-3">
            <div v-if="features.publish" class="flex items-center gap-2">
              <span :class="publishBadgeClass(section)" class="hidden sm:inline-flex rounded-full px-2 py-0.5 text-xs font-medium">
                {{ isPublished(section) ? $t('projects.status_published') : $t('projects.status_draft') }}
              </span>
              <toggle-component :status="isPublished(section)" :product="section.uuid"
                                :label="$t(isPublished(section) ? 'projects.unpublish_section' : 'projects.publish_section')"
                                :callback="(uuid, published) => togglePublished(section, null, published)"/>
            </div>
            <action-menu-component :items="sectionMenuItems(section)" :label="$t('projects.section_actions', { title: section.title })"
                                   :trigger-class="touchMenuTriggerClass" :disabled="general.bulkSectionUuid === section.uuid"
                                   @select="onSectionAction(section, $event)"></action-menu-component>
            </div>
          </div>
        </div>

        <!-- Programación (drip) dentro de la propia sección (FE-062, D5) -->
        <section-schedule-component v-if="scheduleSectionUuid === section.uuid" :section="section"
                                    @close="closeSchedule(section)" @saved="closeSchedule(section)"></section-schedule-component>

        <!-- Lecciones. `group` compartido: se pueden arrastrar a otra sección (también vacía). -->
        <p v-if="section.lessons.length === 0 && !isCollapsed(section)" class="px-4 py-6 text-center text-sm text-gray-600 sm:px-6">
          {{ features.reorder ? $t('projects.empty_lessons_drop') : $t('projects.empty_lessons') }}
        </p>
        <!-- Plegada, la sección sigue siendo destino de arrastre: zona compacta mientras se arrastra. -->
        <vue-draggable
          v-model="section.lessons"
          :id="'section-lessons-' + section.uuid"
          tag="ol"
          group="lessons"
          handle=".drag-handle"
          ghost-class="drag-ghost"
          chosen-class="drag-chosen"
          :class="lessonListClass(section)"
          :animation="150"
          :disabled="!features.reorder || general.savingOrder"
          @start="onDragStart"
          @end="onLessonDragEnd"
        >
          <lesson-row-component v-for="(lesson, lessonIndex) in section.lessons" :key="lesson.uuid"
                                :lesson="lesson" :saved-title="savedLessonTitles[lesson.uuid] ?? lesson.title"
                                :type-label="lessonTypeLabel(lesson.lesson_type)" :features="features"
                                :can-move-up="sectionIndex > 0 || lessonIndex > 0"
                                :can-move-down="sectionIndex < sections.length - 1 || lessonIndex < section.lessons.length - 1"
                                :saving-order="general.savingOrder" :renaming="general.savingLessonUuid === lesson.uuid"
                                :busy="general.deleting || general.duplicatingUuid === lesson.uuid" :just-saved="savedFlashUuid === lesson.uuid"
                                @move="moveLesson(sectionIndex, lessonIndex, $event)"
                                @rename="renameLesson(section, lesson, $event)"
                                @set-published="togglePublished(section, lesson, $event)"
                                @set-free-preview="toggleFreePreview(section, lesson, $event)"
                                @duplicate="duplicateLesson(section, lesson)"
                                @delete="askDeleteLesson(section, lesson)"/>
        </vue-draggable>

        <!-- Nueva lección (formulario en línea al pie de la sección) -->
        <template v-if="!isCollapsed(section)">
        <div v-if="general.createLesson && general.lessonSectionUuid === section.uuid"
             class="rounded-b-lg border-t border-gray-100 bg-gray-50 px-4 py-4 sm:px-6">
          <label :for="'new-lesson-title-' + section.uuid" class="block text-sm font-medium text-gray-900">{{ $t('projects.new_lesson') }}</label>
          <input :id="'new-lesson-title-' + section.uuid" v-model="general.lesson.title" type="text"
                 :placeholder="$t('projects.lesson_title_placeholder')" :disabled="isCreatingLessonIn(section)"
                 :aria-invalid="general.lessonError ? 'true' : 'false'" :aria-describedby="'new-lesson-error-' + section.uuid"
                 @keyup.enter="saveLesson(section)" @keyup.esc="cancelAddLesson"
                 class="mt-2 block w-full rounded-md border-gray-300 shadow-sm placeholder-gray-500 focus:border-bpblue focus:ring-bpblue disabled:bg-gray-100 sm:text-sm">
          <p v-if="general.lessonError" :id="'new-lesson-error-' + section.uuid" class="mt-2 text-sm text-red-600">{{ general.lessonError }}</p>
          <div class="mt-4 flex justify-end gap-2">
            <button type="button" v-on:click="cancelAddLesson" :disabled="isCreatingLessonIn(section)"
                    class="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-bpblue">
              {{ $t('general.cancel') }}
            </button>
            <button type="button" v-on:click="saveLesson(section)" :disabled="isCreatingLessonIn(section)"
                    class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-bpblue hover:bg-blue-500 active:scale-[0.98] disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-bpblue">
              <font-awesome-icon v-if="isCreatingLessonIn(section)" class="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" icon="spinner"/>
              {{ $t('projects.create_lesson') }}
            </button>
          </div>
        </div>
        <div v-else class="rounded-b-lg border-t border-gray-100 px-3 py-2 sm:px-4">
          <button type="button" v-on:click="openCreateLesson(section)" :disabled="isCreatingLessonIn(section)"
                  class="inline-flex items-center rounded-md px-2 py-1.5 text-sm font-medium text-bpblue hover:bg-blue-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-bpblue">
            <font-awesome-icon icon="plus" class="-ml-0.5 mr-1.5 h-3.5 w-3.5" aria-hidden="true"/>
            {{ $t('projects.add_lesson') }}
          </button>
        </div>
        </template>
      </section>
    </vue-draggable>

    <!-- Publicar o despublicar una sección con todas sus lecciones (FE-065) -->
    <modal-component v-if="pendingBulkPublish" :on-cancel="cancelBulkPublish" :on-process="confirmBulkPublish"
                     :on-process-title="pendingBulkPublish.published ? 'projects.publish_section_with_lessons' : 'projects.unpublish_section_with_lessons'">
      <template v-slot:header>{{ bulkPublishTitle }}</template>
      {{ bulkPublishMessage }}
    </modal-component>

    <!-- Confirmación de borrado de sección o lección -->
    <modal-component v-if="pendingDelete" :on-cancel="cancelDelete" :on-process="confirmDelete"
                     on-process-title="projects.delete_confirm_button" :loading="general.deleting">
      <template v-slot:header>{{ deleteModalTitle }}</template>
      {{ deleteModalMessage }}
    </modal-component>
  </div>
</template>

<script>

import ModalComponent from "@/components/modals/ModalComponent";
import ToggleComponent from "@/components/forms/ToggleComponent";
import ReorderButtonsComponent from "@/views/projects/ProjectEdit/Curriculum/ReorderButtonsComponent";
import ActionMenuComponent from "@/components/menus/ActionMenuComponent";
import LessonRowComponent from "@/views/projects/ProjectEdit/Curriculum/LessonRowComponent";
import SectionScheduleComponent from "@/views/projects/ProjectEdit/Curriculum/SectionScheduleComponent";
import {dripLabel} from "@/views/projects/ProjectEdit/Curriculum/dripSchedule";
import lmsFeatures from "@/config/lmsFeatures";
import {useProjectsStore} from "@/stores/projects";
import NotificationService from "@/services/notification/NotificationService";
import { VueDraggable } from 'vue-draggable-plus';



// Tiempo que se ve el «Guardado» junto a una lección.
const SAVED_FLASH_MS = 2500

export default {
  name: "ProjectContentComponent",
  components: {
    ActionMenuComponent,
    ModalComponent,
    LessonRowComponent,
    ReorderButtonsComponent,
    SectionScheduleComponent,
    ToggleComponent,
    VueDraggable
  },
  setup(){
    const projectsStore = useProjectsStore()

    return {
      projectsStore
    }
  },
  beforeUnmount() {
    clearTimeout(this.savedFlashTimer)
  },
  created() {
    this.collapsedSections = this.readCollapsedSections()
  },
  data() {
    return {
      general: {
        // Cargas separadas (FE-060): crear una lección no bloquea crear una sección ni borrar.
        creatingSection: false,
        creatingLessonSectionUuid: null,
        deleting: false,
        // Lección o sección que se está duplicando y sección con publicación en lote en curso (FE-065).
        duplicatingUuid: null,
        bulkSectionUuid: null,
        savingOrder: false,
        dragging: false,
        savingSectionUuid: null,
        savingLessonUuid: null,
        sectionError: '',
        createSection: false,
        createLesson: false,
        lessonSectionUuid: null,
        lessonError: '',
        section: {
          title: "",
        },
        lesson: {
          title: "",
        }
      },
      /*
       * Acciones que dependen de endpoints V2 del backend. Las que aún no están
       * desplegadas se ocultan en lugar de fallar en silencio (`@/config/lmsFeatures`).
       */
      features: {
        deleteSection: lmsFeatures.deleteSection,
        deleteLesson: lmsFeatures.deleteLesson,
        renameLesson: lmsFeatures.renameLesson,
        reorder: lmsFeatures.reorderCurriculum,
        publish: lmsFeatures.publishCurriculum,
        freePreview: lmsFeatures.freePreview,
      },
      // Títulos guardados por uuid, para saber si una sección tiene cambios sin guardar.
      savedSectionTitles: {},
      // Ídem para las lecciones (FE-021).
      savedLessonTitles: {},
      // Publicación en lote pendiente de confirmar: { section, published }
      pendingBulkPublish: null,
      // Borrado pendiente de confirmar: { type: 'section' | 'lesson', section, lesson? }
      pendingDelete: null,
      // Orden del currículo al empezar a arrastrar, para detectar cambios y deshacer.
      dragSnapshot: null,
      // Uuids de las secciones plegadas; se recuerdan por curso en localStorage (FE-061).
      collapsedSections: [],
      // Sección cuyo título se está editando (FE-064: fuera de edición se muestra entero).
      editingSectionUuid: null,
      // Sección con el formulario de programación abierto (FE-062).
      scheduleSectionUuid: null,
      // Feedback discreto (FE-063): texto de la región aria-live y lección con «Guardado».
      liveMessage: '',
      savedFlashUuid: null,
      savedFlashTimer: null,
    }
  },
  computed: {
    // Botón «⋯» con área táctil de 44 px en móvil.
    touchMenuTriggerClass() {
      return 'inline-flex h-11 w-11 items-center justify-center rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 focus:outline-none focus:ring-2 focus:ring-bpblue md:h-8 md:w-8'
    },
    sections() {
      return this.projectsStore.project.sections ?? []
    },
    totalLessons() {
      return this.sections.reduce((total, section) => total + (section.lessons?.length ?? 0), 0)
    },
    bulkPublishTitle() {
      if (!this.pendingBulkPublish) return ''
      const {section, published} = this.pendingBulkPublish
      return this.$t(published ? 'projects.publish_section_with_lessons_title' : 'projects.unpublish_section_with_lessons_title', {title: section.title})
    },
    bulkPublishMessage() {
      if (!this.pendingBulkPublish) return ''
      const {section, published} = this.pendingBulkPublish
      const count = this.lessonsToChange(section, published).length
      return this.$t(published ? 'projects.publish_section_with_lessons_message' : 'projects.unpublish_section_with_lessons_message', {count}, count)
    },
    deleteModalTitle() {
      if (!this.pendingDelete) return ''
      return this.pendingDelete.type === 'section'
          ? this.$t('projects.delete_section_title', {title: this.pendingDelete.section.title})
          : this.$t('projects.delete_lesson_title', {title: this.pendingDelete.lesson.title})
    },
    deleteModalMessage() {
      if (!this.pendingDelete) return ''
      if (this.pendingDelete.type === 'lesson') return this.$t('projects.delete_lesson_message')

      const lessonsCount = this.pendingDelete.section.lessons?.length ?? 0
      return lessonsCount
          ? this.$t('projects.delete_section_with_lessons_message', {count: lessonsCount})
          : this.$t('projects.delete_section_message')
    }
  },
  watch: {
    'projectsStore.project.sections': {
      handler(sections) {
        this.savedSectionTitles = Object.fromEntries((sections ?? []).map((section) => [section.uuid, section.title]))
        this.savedLessonTitles = Object.fromEntries((sections ?? [])
            .flatMap((section) => section.lessons ?? [])
            .map((lesson) => [lesson.uuid, lesson.title]))
      },
      immediate: true
    }
  },
  methods: {

    collapsedStorageKey() {
      return 'bp:curriculum-collapsed:' + this.$route.params.id
    },
    readCollapsedSections() {
      try {
        const stored = JSON.parse(window.localStorage.getItem(this.collapsedStorageKey()) ?? '[]')
        return Array.isArray(stored) ? stored : []
      } catch {
        return []
      }
    },
    // Sin localStorage (modo privado, cuota) se pliega igual, solo que no se recuerda.
    setCollapsedSections(uuids) {
      this.collapsedSections = uuids
      try {
        window.localStorage.setItem(this.collapsedStorageKey(), JSON.stringify(uuids))
      } catch {
        // Sin persistencia.
      }
    },
    isCollapsed(section) {
      return this.collapsedSections.includes(section.uuid)
    },
    toggleCollapsed(section) {
      this.setCollapsedSections(this.isCollapsed(section)
          ? this.collapsedSections.filter((uuid) => uuid !== section.uuid)
          : [...this.collapsedSections, section.uuid])
    },
    expandSection(section) {
      if (this.isCollapsed(section)) this.toggleCollapsed(section)
    },
    setAllCollapsed(collapsed) {
      this.setCollapsedSections(collapsed ? this.sections.map((section) => section.uuid) : [])
    },
    sectionSummary(section) {
      const total = section.lessons?.length ?? 0
      const parts = [this.$t('projects.lessons_count', {count: total}, total)]
      if (this.features.publish && total) {
        const published = section.lessons.filter((lesson) => this.isPublished(lesson)).length
        parts.push(this.$t('projects.lessons_published_count', {count: published}, published))
      }
      return parts.join(' · ')
    },
    lessonListClass(section) {
      if (!this.isCollapsed(section)) {
        return ['divide-y divide-gray-100', {'min-h-[1rem]': this.features.reorder}]
      }
      return ['lessons-collapsed', {'m-2 min-h-[2.5rem] rounded-md border-2 border-dashed border-gray-300': this.general.dragging}]
    },
    sectionMenuItems(section) {
      const publishItems = this.features.publish ? [
        {key: 'publish-all', label: this.$t('projects.publish_section_with_lessons'), icon: 'eye',
          disabled: this.isPublished(section) && !this.lessonsToChange(section, true).length},
        {key: 'unpublish-all', label: this.$t('projects.unpublish_section_with_lessons'), icon: 'eye-slash',
          disabled: !this.isPublished(section) && !this.lessonsToChange(section, false).length},
      ] : []

      return [
        {key: 'add-lesson', label: this.$t('projects.add_lesson'), icon: 'plus'},
        {key: 'schedule', label: this.$t('projects.drip_menu'), icon: 'clock'},
        {key: 'duplicate', label: this.$t('projects.duplicate_section'), icon: 'copy', disabled: this.general.duplicatingUuid === section.uuid,
          description: this.$t('projects.duplicate_section_help')},
        ...publishItems,
        ...(this.features.deleteSection
            ? [{key: 'delete', label: this.$t('projects.delete_section'), icon: 'trash', danger: true, disabled: this.general.deleting}]
            : []),
      ]
    },
    // Lecciones de la sección que cambiarían al publicar o despublicar en lote.
    lessonsToChange(section, published) {
      return (section.lessons ?? []).filter((lesson) => this.isPublished(lesson) !== published)
    },
    onSectionAction(section, action) {
      if (action === 'add-lesson') {
        this.expandSection(section)
        this.openCreateLesson(section)
      }
      if (action === 'schedule') this.openSchedule(section)
      if (action === 'duplicate') this.duplicateSection(section)
      if (action === 'publish-all' || action === 'unpublish-all') {
        this.pendingBulkPublish = {section, published: action === 'publish-all'}
      }
      if (action === 'delete') this.askDeleteSection(section)
    },
    dripLabel(section) {
      return dripLabel(section, this.$t, this.$i18n.locale)
    },
    openSchedule(section) {
      this.scheduleSectionUuid = section.uuid
    },
    // Al cerrar, el foco vuelve al menú «⋯» de la sección para no perderlo.
    closeSchedule(section) {
      this.scheduleSectionUuid = null
      this.$nextTick(() => {
        document.querySelector('#section-header-' + section.uuid + ' [aria-haspopup="menu"]')?.focus()
      })
    },

    isEditingSectionTitle(section) {
      return this.editingSectionUuid === section.uuid || this.isSectionDirty(section)
    },
    startEditSectionTitle(section) {
      this.editingSectionUuid = section.uuid
      this.$nextTick(() => document.getElementById('section-title-' + section.uuid)?.focus())
    },
    stopEditSectionTitle(section) {
      if (this.editingSectionUuid === section.uuid) this.editingSectionUuid = null
    },
    revertSectionTitle(section) {
      section.title = this.savedSectionTitles[section.uuid]
      this.editingSectionUuid = null
      this.$nextTick(() => document.getElementById('section-title-display-' + section.uuid)?.focus())
    },
    isSectionDirty(section) {
      return section.title !== this.savedSectionTitles[section.uuid]
    },
    editSection(section) {
      this.general.savingSectionUuid = section.uuid

      this.projectsStore.editSection({
        project_uuid: this.$route.params.id,
        uuid: section.uuid,
        title: section.title.trim(),
        order: section.order
      })
          .then(() => {
            section.title = section.title.trim()
            this.savedSectionTitles[section.uuid] = section.title
            NotificationService.showNotification(this.$t('projects.section_saved'), "success")
          })
          .catch((error) => {
            NotificationService.showApiError(error, this.$t('projects.unexpected_error'))
          })
          .finally(() => {
            this.general.savingSectionUuid = null
          })
    },
    renameLesson(section, lesson, newTitle) {
      const title = newTitle.trim()
      if (!title || title === this.savedLessonTitles[lesson.uuid]) return

      this.general.savingLessonUuid = lesson.uuid

      this.projectsStore.renameLesson(section.uuid, lesson.uuid, title)
          .then(() => {
            lesson.title = title
            this.savedLessonTitles[lesson.uuid] = title
            this.announceSaved(this.$t('projects.lesson_saved'), lesson.uuid)
          })
          .catch((error) => {
            NotificationService.showApiError(error, this.$t('projects.unexpected_error'))
          })
          .finally(() => {
            this.general.savingLessonUuid = null
          })
    },
    // Éxito sin toast: se anuncia por aria-live y, si es una lección, se marca con «Guardado».
    announceSaved(message, lessonUuid = null) {
      this.liveMessage = ''
      this.$nextTick(() => {
        this.liveMessage = message
      })
      clearTimeout(this.savedFlashTimer)
      this.savedFlashUuid = lessonUuid
      this.savedFlashTimer = setTimeout(() => {
        this.savedFlashUuid = null
      }, SAVED_FLASH_MS)
    },
    // Tras mover con los botones, el foco vuelve al mismo botón en la nueva posición.
    refocusMoveButton(rowId, label, fallbackLabel) {
      this.$nextTick(() => {
        const row = document.getElementById(rowId)
        if (!row) return
        const button = row.querySelector(`button[aria-label="${label}"]:not(:disabled)`)
            ?? row.querySelector(`button[aria-label="${fallbackLabel}"]:not(:disabled)`)
        button?.focus()
      })
    },
    askDeleteSection(section) {
      this.pendingDelete = {type: 'section', section}
    },
    askDeleteLesson(section, lesson) {
      this.pendingDelete = {type: 'lesson', section, lesson}
    },
    cancelDelete() {
      if (!this.general.deleting) this.pendingDelete = null
    },
    confirmDelete() {
      const {type, section, lesson} = this.pendingDelete
      this.general.deleting = true

      // Si la sección tiene lecciones, el usuario ya ha visto el aviso: se borran en cascada.
      const request = type === 'section'
          ? this.projectsStore.deleteSection(section.uuid, (section.lessons?.length ?? 0) > 0)
          : this.projectsStore.deleteLesson(section.uuid, lesson.uuid)

      request
          .then(() => {
            const message = type === 'section' ? 'projects.section_deleted' : 'projects.lesson_deleted'
            NotificationService.showNotification(this.$t(message), "success")
            this.pendingDelete = null
          })
          .catch((error) => {
            NotificationService.showApiError(error, this.$t('projects.unexpected_error'))
          })
          .finally(() => {
            this.general.deleting = false
          })
    },
    openCreateSection() {
      this.general.createSection = true
      this.general.sectionError = ''
      this.$nextTick(() => this.$refs.newSectionTitle?.focus())
    },
    cancelAddSection() {
      this.general.createSection = false
      this.general.section.title = ''
      this.general.sectionError = ''
    },
    saveSection() {
      const title = this.general.section.title.trim()
      if (!title) {
        this.general.sectionError = this.$t('projects.section_title_required')
        return
      }

      this.general.creatingSection = true
      this.general.sectionError = ''

      this.projectsStore.addSection(title)
          .then((section) => {
            this.savedSectionTitles[section.uuid] = section.title
            this.cancelAddSection()
            this.announceSaved(this.$t('projects.section_created'))
            this.focusNewTitle('section-title-display-' + section.uuid)
          })
          .catch((error) => {
            NotificationService.showApiError(error, this.$t('projects.unexpected_error'))
          })
          .finally(() => {
            this.general.creatingSection = false
          })
    },
    // Tras crear, el foco va al título nuevo (clic o Enter para renombrarlo) y se hace visible.
    focusNewTitle(elementId) {
      this.$nextTick(() => {
        const element = document.getElementById(elementId)
        if (!element) return
        const smooth = !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
        element.scrollIntoView({behavior: smooth ? 'smooth' : 'auto', block: 'center'})
        element.focus({preventScroll: true})
      })
    },
    lessonTypeLabel(lessonType) {
      if (!lessonType) return ''
      const translations = lessonType.translations
      return (translations && typeof translations === 'object' && translations[this.$i18n.locale]) || lessonType.title
    },
    isCreatingLessonIn(section) {
      return this.general.creatingLessonSectionUuid === section.uuid
    },
    openCreateLesson(section) {
      this.general.createLesson = true
      this.general.lessonSectionUuid = section.uuid
      this.general.lesson.title = ''
      this.general.lessonError = ''
      this.$nextTick(() => document.getElementById('new-lesson-title-' + section.uuid)?.focus())
    },
    cancelAddLesson() {
      this.general.createLesson = false
      this.general.lessonSectionUuid = null
      this.general.lessonError = ''
    },
    saveLesson(section) {
      const title = this.general.lesson.title.trim()
      if (!title) {
        this.general.lessonError = this.$t('projects.lesson_title_required')
        return
      }

      this.general.creatingLessonSectionUuid = section.uuid
      this.general.lessonError = ''

      this.projectsStore.addLesson(section, title)
          .then((lesson) => {
            this.savedLessonTitles[lesson.uuid] = lesson.title
            this.cancelAddLesson()
            this.announceSaved(this.$t('projects.lesson_created'), lesson.uuid)
            this.focusNewTitle('lesson-title-display-' + lesson.uuid)
          })
          .catch((error) => {
            NotificationService.showApiError(error, this.$t('projects.unexpected_error'))
          })
          .finally(() => {
            this.general.creatingLessonSectionUuid = null
          })
    },

    duplicateLesson(section, lesson) {
      this.general.duplicatingUuid = lesson.uuid
      this.projectsStore.duplicateLesson(section, lesson)
          .then((copy) => {
            this.savedLessonTitles[copy.uuid] = copy.title
            this.announceSaved(this.$t('projects.lesson_duplicated'), copy.uuid)
            this.focusNewTitle('lesson-title-display-' + copy.uuid)
          })
          .catch((error) => {
            NotificationService.showApiError(error, this.$t('projects.unexpected_error'))
          })
          .finally(() => {
            this.general.duplicatingUuid = null
          })
    },
    duplicateSection(section) {
      this.general.duplicatingUuid = section.uuid
      this.projectsStore.duplicateSection(section)
          .then((copy) => {
            this.savedSectionTitles[copy.uuid] = copy.title
            copy.lessons.forEach((lesson) => {
              this.savedLessonTitles[lesson.uuid] = lesson.title
            })
            this.announceSaved(this.$t('projects.section_duplicated'))
            this.focusNewTitle('section-title-display-' + copy.uuid)
          })
          .catch((error) => {
            NotificationService.showApiError(error, this.$t('projects.unexpected_error'))
          })
          .finally(() => {
            this.general.duplicatingUuid = null
          })
    },
    cancelBulkPublish() {
      this.pendingBulkPublish = null
    },
    confirmBulkPublish() {
      const {section, published} = this.pendingBulkPublish
      this.pendingBulkPublish = null
      this.general.bulkSectionUuid = section.uuid

      this.projectsStore.setSectionPublishedWithLessons(section, published)
          .then((failed) => {
            if (failed) {
              NotificationService.showNotification(this.$t('projects.bulk_publish_partial_error', {count: failed}, failed), "error")
              return
            }
            this.announceSaved(this.$t(published ? 'projects.section_with_lessons_published' : 'projects.section_with_lessons_unpublished'))
          })
          .finally(() => {
            this.general.bulkSectionUuid = null
          })
    },
    isPublished(item) {
      return !!item.published_at
    },
    publishBadgeClass(item) {
      return this.isPublished(item) ? 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20' : 'bg-gray-100 text-gray-700 ring-1 ring-inset ring-gray-500/10'
    },
    // `lesson` a null publica/despublica la sección.
    togglePublished(section, lesson, published) {
      const request = lesson
          ? this.projectsStore.setLessonPublished(section, lesson, published)
          : this.projectsStore.setSectionPublished(section, published)
      const type = lesson ? 'lesson' : 'section'

      request
          .then(() => {
            this.announceSaved(this.$t(`projects.${type}_${published ? 'published' : 'unpublished'}`), lesson?.uuid ?? null)
          })
          .catch((error) => {
            NotificationService.showApiError(error, this.$t('projects.unexpected_error'))
          })
    },
    toggleFreePreview(section, lesson, isFreePreview) {
      this.projectsStore.setLessonFreePreview(section, lesson, isFreePreview)
          .then(() => {
            this.announceSaved(this.$t(isFreePreview ? 'projects.free_preview_enabled' : 'projects.free_preview_disabled'), lesson.uuid)
          })
          .catch((error) => {
            NotificationService.showApiError(error, this.$t('projects.unexpected_error'))
          })
    },
    onDragStart() {
      this.dragSnapshot = this.projectsStore.curriculumOrder()
      this.general.dragging = true
    },
    onSectionDragEnd() {
      this.general.dragging = false
      this.persistOrder('sections', this.dragSnapshot)
    },
    onLessonDragEnd() {
      this.general.dragging = false
      this.persistOrder('lessons', this.dragSnapshot)
    },
    moveSection(sectionIndex, delta) {
      const previousOrder = this.projectsStore.curriculumOrder()
      const sections = this.projectsStore.project.sections
      const [section] = sections.splice(sectionIndex, 1)
      sections.splice(sectionIndex + delta, 0, section)
      const [label, fallback] = this.moveLabels('section', delta)
      this.persistOrder('sections', previousOrder)
          .then(() => this.refocusMoveButton('section-header-' + section.uuid, label, fallback))
    },
    // En los extremos de una sección, la lección pasa a la sección anterior o siguiente.
    moveLesson(sectionIndex, lessonIndex, delta) {
      const previousOrder = this.projectsStore.curriculumOrder()
      const sections = this.projectsStore.project.sections
      const lessons = sections[sectionIndex].lessons
      const targetIndex = lessonIndex + delta
      const [lesson] = lessons.splice(lessonIndex, 1)

      // Si pasa a una sección plegada, se despliega para no perder de vista la lección.
      if (targetIndex < 0) {
        sections[sectionIndex - 1].lessons.push(lesson)
        this.expandSection(sections[sectionIndex - 1])
      } else if (targetIndex > lessons.length) {
        sections[sectionIndex + 1].lessons.unshift(lesson)
        this.expandSection(sections[sectionIndex + 1])
      } else {
        lessons.splice(targetIndex, 0, lesson)
      }
      const [label, fallback] = this.moveLabels('lesson', delta)
      this.persistOrder('lessons', previousOrder, lesson.uuid)
          .then(() => this.refocusMoveButton('lesson-row-' + lesson.uuid, label, fallback))
    },
    // Etiquetas de los botones subir/bajar: la del mismo sentido y, si queda deshabilitado, la contraria.
    moveLabels(kind, delta) {
      const up = this.$t(`projects.move_${kind}_up`)
      const down = this.$t(`projects.move_${kind}_down`)
      return delta < 0 ? [up, down] : [down, up]
    },
    // Devuelve una promesa que no falla: sirve para devolver el foco cuando termina.
    persistOrder(kind, previousOrder, movedLessonUuid = null) {
      this.dragSnapshot = null
      if (!previousOrder || JSON.stringify(previousOrder) === JSON.stringify(this.projectsStore.curriculumOrder())) {
        return Promise.resolve()
      }

      const isSections = kind === 'sections'
      this.general.savingOrder = true
      const request = isSections
          ? this.projectsStore.saveSectionsOrder(previousOrder)
          : this.projectsStore.saveCurriculumOrder(previousOrder)

      return request
          .then(() => {
            this.announceSaved(this.$t(isSections ? 'projects.sections_order_saved' : 'projects.lessons_order_saved'), movedLessonUuid)
          })
          .catch((error) => {
            NotificationService.showApiError(error, this.$t(isSections ? 'projects.sections_order_error' : 'projects.lessons_order_error'))
          })
          .finally(() => {
            this.general.savingOrder = false
          })
    },
  },
}
</script>

<style scoped>
/* Hueco de destino mientras se arrastra (SortableJS añade la clase en tiempo de ejecución). */
:deep(.drag-ghost) {
  opacity: 0.4;
  background: #eff6ff;
  outline: 2px dashed #93c5fd;
  outline-offset: -2px;
}

/* Sección plegada (FE-061): sus lecciones no se ven, salvo la que se está soltando. */
.lessons-collapsed > li:not(.drag-ghost) {
  display: none;
}

/* Elemento que se está arrastrando. */
:deep(.drag-chosen) {
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
}
</style>
